import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { MAUZAS } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Briefcase, Send, MapPin, Phone, Calendar, FileText, Clock, CheckCircle2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ApplicationDetailsDialog from '../components/ApplicationDetailsDialog';

const JakrikraDashboard = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const myMauza = MAUZAS.find((m) => m.id === user.mauzaHandled);

  const fetchApps = () => {
    setLoading(true);
    api.get('/applications').then(({ data }) => setApps(data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchApps(); }, []);

  const pending = apps.filter((a) => a.status === 'pending_jakrikra');
  const forwarded = apps.filter((a) => a.status === 'forwarded_nokma');
  const completed = apps.filter((a) => a.status === 'approved' || a.status === 'rejected');

  const groupedPending = pending.reduce((acc, a) => {
    (acc[a.akhing] = acc[a.akhing] || []).push(a);
    return acc;
  }, {});

  const forward = async (id) => {
    try {
      await api.put(`/applications/${id}/forward`);
      toast.success('Forwarded to Nokma Sani');
      fetchApps();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const forwardAll = async (akhing) => {
    const list = pending.filter((a) => a.akhing === akhing);
    let count = 0;
    for (const a of list) {
      try { await api.put(`/applications/${a.id}/forward`); count++; } catch (_) {}
    }
    toast.success(`${count} application(s) forwarded`);
    fetchApps();
  };

  const Row = ({ a, action }) => (
    <div className="border border-border rounded-lg p-4 bg-card flex flex-wrap items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Badge variant="outline" className="font-mono text-xs">{a.id}</Badge>
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">{a.typeLabel}</Badge>
        </div>
        <div className="font-medium">{a.applicantName}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
          <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{a.applicantMobile}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{a.akhing}</span>
          {a.days && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{a.days} days</span>}
          {a.purpose && <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{a.purpose}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setViewing(a)} className="gap-1.5">
          <Eye className="h-4 w-4" /> View
        </Button>
        {action}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-primary" /> Office of {user.name}</div>
          <h1 className="font-serif text-3xl font-bold mt-1">{user.name} · Room</h1>
          <div className="text-sm text-muted-foreground mt-1">Handles: {myMauza?.label || user.mauzaHandled}</div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {[
            { l: 'Pending Review', v: pending.length, icon: Clock, c: 'text-amber-600' },
            { l: 'Forwarded', v: forwarded.length, icon: Send, c: 'text-blue-600' },
            { l: 'Completed', v: completed.length, icon: CheckCircle2, c: 'text-emerald-600' },
          ].map((s) => (
            <Card key={s.l} className="border-border">
              <CardContent className="pt-6 flex items-center gap-4">
                <div className={`h-12 w-12 rounded-lg bg-secondary flex items-center justify-center ${s.c}`}>
                  <s.icon className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{s.v}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{s.l}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-serif">Applicant List</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-12 text-muted-foreground">Loading...</div> : (
              <Tabs defaultValue="pending">
                <TabsList>
                  <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
                  <TabsTrigger value="forwarded">Forwarded ({forwarded.length})</TabsTrigger>
                  <TabsTrigger value="completed">Completed ({completed.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="pending" className="mt-4">
                  {Object.keys(groupedPending).length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No pending applications</div>
                  ) : Object.entries(groupedPending).map(([ak, list]) => (
                    <div key={ak} className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-serif text-lg font-semibold flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-primary" /> {ak} <Badge variant="outline">{list.length}</Badge>
                        </div>
                        <Button size="sm" onClick={() => forwardAll(ak)} className="gap-1.5 bg-primary hover:bg-primary/90">
                          <Send className="h-4 w-4" /> Forward All to Nokma
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {list.map((a) => (
                          <Row key={a.id} a={a} action={
                            <Button size="sm" onClick={() => forward(a.id)} className="gap-1.5 bg-accent hover:bg-accent/90">
                              <Send className="h-4 w-4" /> Forward
                            </Button>
                          } />
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="forwarded" className="mt-4">
                  <div className="space-y-2">
                    {forwarded.length === 0 ? <div className="text-center py-12 text-muted-foreground">Nothing forwarded</div> :
                      forwarded.map((a) => <Row key={a.id} a={a} action={<Badge className="bg-blue-100 text-blue-800 border-blue-200" variant="outline">Awaiting Nokma</Badge>} />)}
                  </div>
                </TabsContent>
                <TabsContent value="completed" className="mt-4">
                  <div className="space-y-2">
                    {completed.length === 0 ? <div className="text-center py-12 text-muted-foreground">No completed yet</div> :
                      completed.map((a) => <Row key={a.id} a={a} action={
                        a.status === 'approved'
                          ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Approved</Badge>
                          : <Badge className="bg-rose-100 text-rose-800 border-rose-200" variant="outline">Rejected</Badge>
                      } />)}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        <ApplicationDetailsDialog app={viewing} open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} />
      </main>
      <Footer />
    </div>
  );
};

export default JakrikraDashboard;
